import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { rmApi as R, CATEGORY_LABEL, inr } from "@/lib/rawMaterials";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, PackagePlus, Send, SlidersHorizontal, AlertTriangle } from "lucide-react";

export default function RawMaterials() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState("stock");
  const canCreate = can("raw_materials", "create");
  const canApprove = can("raw_materials", "approve");
  const load = () => R.stats().then(setStats).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="raw-materials-page">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Raw Material Stock</h2>
        <p className="mt-1 text-sm text-slate-500">Purchased materials used before production — separate from Finished Goods Inventory. Stock reduces only when issued to production.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat testid="rm-stat-materials" label="Materials" value={stats.materials ?? 0} />
        <Stat testid="rm-stat-value" label="Stock value" value={inr(stats.total_value)} accent="text-emerald-600" />
        <Stat testid="rm-stat-low" label="Low stock" value={stats.low_stock ?? 0} accent="text-rose-600" />
        <Stat testid="rm-stat-pending" label="Pending adj." value={stats.pending_adjustments ?? 0} accent="text-amber-600" />
        <Stat testid="rm-stat-issues" label="Issues" value={stats.issue_count ?? 0} accent="text-indigo-600" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="rm-tabs">
          <TabsTrigger value="stock" data-testid="rm-tab-stock">Current Stock</TabsTrigger>
          <TabsTrigger value="materials" data-testid="rm-tab-materials">Materials</TabsTrigger>
          <TabsTrigger value="grn" data-testid="rm-tab-grn">Goods Receipt</TabsTrigger>
          <TabsTrigger value="issues" data-testid="rm-tab-issues">Issues</TabsTrigger>
          <TabsTrigger value="adjustments" data-testid="rm-tab-adjustments">Adjustments</TabsTrigger>
          <TabsTrigger value="reports" data-testid="rm-tab-reports">Consumption</TabsTrigger>
        </TabsList>
        <TabsContent value="stock" className="mt-4"><StockTab navigate={navigate} /></TabsContent>
        <TabsContent value="materials" className="mt-4"><MaterialsTab navigate={navigate} canCreate={canCreate} onChange={load} /></TabsContent>
        <TabsContent value="grn" className="mt-4"><GrnTab canCreate={canCreate} onChange={load} /></TabsContent>
        <TabsContent value="issues" className="mt-4"><IssuesTab canCreate={canCreate} onChange={load} /></TabsContent>
        <TabsContent value="adjustments" className="mt-4"><AdjustmentsTab canCreate={canCreate} canApprove={canApprove} onChange={load} /></TabsContent>
        <TabsContent value="reports" className="mt-4"><ConsumptionTab /></TabsContent>
      </Tabs>
    </div>
  );
}

const Stat = ({ label, value, accent = "text-slate-900", testid }) => (
  <Card className="p-4" data-testid={testid}><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p><p className={`mt-1 font-heading text-xl font-bold ${accent}`}>{value}</p></Card>
);

function useMaterials() {
  const [mats, setMats] = useState([]);
  useEffect(() => { R.listMaterials().then(setMats).catch(() => {}); }, []);
  return mats;
}

function StockTab({ navigate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { R.currentStock().then(setRows).finally(() => setLoading(false)); }, []);
  if (loading) return <Spin />;
  return (
    <Card className="p-4">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <Th>Material</Th><Th>Category</Th><Th right>On hand</Th><Th right>Reorder</Th><Th right>Avg rate</Th><Th right>Value</Th><Th>Status</Th>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/raw-materials/${r.id}`)} data-testid={`stock-row-${r.code}`}>
                <TableCell className="font-medium text-slate-800">{r.name}<span className="block text-xs text-slate-400">{r.code}</span></TableCell>
                <TableCell className="text-slate-600">{CATEGORY_LABEL[r.category]}</TableCell>
                <TableCell className="text-right tabular-nums text-slate-700">{r.on_hand} {r.unit}</TableCell>
                <TableCell className="text-right tabular-nums text-slate-500">{r.reorder_level || "—"}</TableCell>
                <TableCell className="text-right tabular-nums text-slate-600">{inr(r.avg_rate)}</TableCell>
                <TableCell className="text-right tabular-nums text-slate-700">{inr(r.value)}</TableCell>
                <TableCell>{r.low ? <Badge className="bg-rose-50 text-rose-700 hover:bg-rose-50"><AlertTriangle className="mr-1 h-3 w-3" />Low</Badge> : <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">OK</Badge>}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-12 text-center text-slate-400" data-testid="stock-empty">No materials yet. Add a material, then receive stock via GRN.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function MaterialsTab({ navigate, canCreate, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = () => { setLoading(true); R.listMaterials().then(setRows).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  return (
    <Card className="p-4">
      <div className="mb-4 flex justify-end">
        {canCreate && <Button onClick={() => setOpen(true)} data-testid="add-material-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-2 h-4 w-4" />Add Material</Button>}
      </div>
      {loading ? <Spin /> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><Th>Code</Th><Th>Name</Th><Th>Category</Th><Th>Unit</Th><Th right>On hand</Th></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/raw-materials/${r.id}`)} data-testid={`material-row-${r.code}`}>
                  <TableCell className="font-medium text-slate-800">{r.code}</TableCell>
                  <TableCell className="text-slate-700">{r.name}</TableCell>
                  <TableCell className="text-slate-600">{CATEGORY_LABEL[r.category]}</TableCell>
                  <TableCell className="text-slate-600">{r.unit}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{r.on_hand}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-slate-400">No materials defined.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <MaterialDialog open={open} onOpenChange={setOpen} onDone={() => { load(); onChange?.(); }} />
    </Card>
  );
}

function MaterialDialog({ open, onOpenChange, onDone }) {
  const [meta, setMeta] = useState({ categories: {} });
  const [f, setF] = useState({ name: "", category: "base_fabric", unit: "", reorder_level: "", default_rate: "", hsn: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { R.meta().then(setMeta); setF({ name: "", category: "base_fabric", unit: "", reorder_level: "", default_rate: "", hsn: "" }); } }, [open]);
  const catUnit = meta.categories?.[f.category]?.unit || "";
  const submit = async () => {
    if (!f.name) { toast.error("Name required"); return; }
    setBusy(true);
    try { await R.createMaterial({ name: f.name, category: f.category, unit: f.unit || catUnit, reorder_level: Number(f.reorder_level) || 0, default_rate: Number(f.default_rate) || 0, hsn: f.hsn }); toast.success("Material added"); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add Material</DialogTitle><DialogDescription>Define a raw material in the master. Stock starts at zero and grows via GRN.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="material-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v, unit: "" })}>
                <SelectTrigger data-testid="material-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(meta.categories).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Unit</Label><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder={catUnit} data-testid="material-unit-input" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Reorder level</Label><Input type="number" value={f.reorder_level} onChange={(e) => setF({ ...f, reorder_level: e.target.value })} data-testid="material-reorder-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Default rate</Label><Input type="number" value={f.default_rate} onChange={(e) => setF({ ...f, default_rate: e.target.value })} data-testid="material-rate-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">HSN</Label><Input value={f.hsn} onChange={(e) => setF({ ...f, hsn: e.target.value })} data-testid="material-hsn-input" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="submit-material-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GrnTab({ canCreate, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = () => { setLoading(true); R.listGrn().then(setRows).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  return (
    <Card className="p-4">
      <div className="mb-4 flex justify-end">{canCreate && <Button onClick={() => setOpen(true)} data-testid="add-grn-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><PackagePlus className="mr-2 h-4 w-4" />Receive Stock (GRN)</Button>}</div>
      {loading ? <Spin /> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><Th>GRN</Th><Th>Material</Th><Th>Supplier</Th><Th right>Qty</Th><Th right>Rate</Th><Th right>Amount</Th></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`grn-row-${r.grn_no}`}>
                  <TableCell className="font-medium text-slate-800">{r.grn_no}<span className="block text-xs text-slate-400">{r.received_date}</span></TableCell>
                  <TableCell className="text-slate-700">{r.material_name}</TableCell>
                  <TableCell className="text-slate-600">{r.supplier_name || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{r.qty} {r.unit}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-600">{inr(r.rate)}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{inr(r.amount)}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-400" data-testid="grn-empty">No goods receipts yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <GrnDialog open={open} onOpenChange={setOpen} onDone={() => { load(); onChange?.(); }} />
    </Card>
  );
}

function GrnDialog({ open, onOpenChange, onDone }) {
  const mats = useMaterials();
  const [f, setF] = useState({ material_id: "", qty: "", rate: "", supplier_name: "", po_ref: "", invoice_ref: "", lot_no: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ material_id: "", qty: "", rate: "", supplier_name: "", po_ref: "", invoice_ref: "", lot_no: "" }); }, [open]);
  const submit = async () => {
    if (!f.material_id || !(Number(f.qty) > 0)) { toast.error("Select material and quantity"); return; }
    setBusy(true);
    try { const r = await R.createGrn({ ...f, qty: Number(f.qty), rate: Number(f.rate) || 0 }); toast.success(`${r.grn_no} received`); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Goods Receipt (GRN)</DialogTitle><DialogDescription>Receive purchased material into raw stock. Increases on-hand and recomputes the weighted average rate.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Material</Label>
            <Select value={f.material_id} onValueChange={(v) => setF({ ...f, material_id: v })}>
              <SelectTrigger data-testid="grn-material-select"><SelectValue placeholder="Select material" /></SelectTrigger>
              <SelectContent>{mats.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.unit})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Quantity</Label><Input type="number" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="grn-qty-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Rate</Label><Input type="number" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} data-testid="grn-rate-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Supplier</Label><Input value={f.supplier_name} onChange={(e) => setF({ ...f, supplier_name: e.target.value })} data-testid="grn-supplier-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Lot / Roll ID</Label><Input value={f.lot_no} onChange={(e) => setF({ ...f, lot_no: e.target.value })} data-testid="grn-lot-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">PO ref</Label><Input value={f.po_ref} onChange={(e) => setF({ ...f, po_ref: e.target.value })} data-testid="grn-po-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Invoice ref</Label><Input value={f.invoice_ref} onChange={(e) => setF({ ...f, invoice_ref: e.target.value })} data-testid="grn-invoice-input" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="submit-grn-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Receive"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IssuesTab({ canCreate, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = () => { setLoading(true); R.listIssues().then(setRows).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  return (
    <Card className="p-4">
      <div className="mb-4 flex justify-end">{canCreate && <Button onClick={() => setOpen(true)} data-testid="add-issue-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Send className="mr-2 h-4 w-4" />Issue to Production</Button>}</div>
      {loading ? <Spin /> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><Th>Issue</Th><Th>Material</Th><Th>Job / Stage</Th><Th right>Qty</Th><Th right>Value</Th></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`issue-row-${r.issue_no}`}>
                  <TableCell className="font-medium text-slate-800">{r.issue_no}<span className="block text-xs text-slate-400">{(r.created_at || "").slice(0, 10)}</span></TableCell>
                  <TableCell className="text-slate-700">{r.material_name}</TableCell>
                  <TableCell className="text-slate-600">{r.job_no || "—"} · {r.stage}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{r.qty} {r.unit}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-600">{inr(r.value)}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-slate-400" data-testid="issue-empty">No issues yet. Issuing reduces raw stock.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <IssueDialog open={open} onOpenChange={setOpen} onDone={() => { load(); onChange?.(); }} />
    </Card>
  );
}

function IssueDialog({ open, onOpenChange, onDone }) {
  const mats = useMaterials();
  const [jobs, setJobs] = useState([]);
  const [f, setF] = useState({ material_id: "", qty: "", job_no: "", stage: "printing", purpose: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { R.productionJobs().then(setJobs); setF({ material_id: "", qty: "", job_no: "", stage: "printing", purpose: "" }); } }, [open]);
  const mat = mats.find((m) => m.id === f.material_id);
  const submit = async () => {
    if (!f.material_id || !(Number(f.qty) > 0)) { toast.error("Select material and quantity"); return; }
    setBusy(true);
    try { const r = await R.createIssue({ material_id: f.material_id, qty: Number(f.qty), job_no: f.job_no || null, stage: f.stage, purpose: f.purpose }); toast.success(`${r.issue_no} issued`); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Issue to Production</DialogTitle><DialogDescription>Reduces raw stock. Sublimation paper → Printing; base fabric → Fusing.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Material</Label>
            <Select value={f.material_id} onValueChange={(v) => setF({ ...f, material_id: v })}>
              <SelectTrigger data-testid="issue-material-select"><SelectValue placeholder="Select material" /></SelectTrigger>
              <SelectContent>{mats.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} — {m.on_hand} {m.unit} available</SelectItem>)}</SelectContent>
            </Select>
            {mat && <p className="text-xs text-slate-500">In stock: {mat.on_hand} {mat.unit}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Quantity</Label><Input type="number" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="issue-qty-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Stage</Label>
              <Select value={f.stage} onValueChange={(v) => setF({ ...f, stage: v })}>
                <SelectTrigger data-testid="issue-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="printing">Printing</SelectItem><SelectItem value="fusing">Fusing</SelectItem><SelectItem value="packing">Packing</SelectItem><SelectItem value="general">General</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Production job (optional)</Label>
            <Select value={f.job_no} onValueChange={(v) => setF({ ...f, job_no: v })}>
              <SelectTrigger data-testid="issue-job-select"><SelectValue placeholder="Link to a job" /></SelectTrigger>
              <SelectContent>{jobs.map((j) => <SelectItem key={j.job_no} value={j.job_no}>{j.job_no} · {typeof j.customer === "string" ? j.customer : (j.customer?.company_name || j.customer?.name || "")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Purpose (optional)</Label><Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="issue-purpose-input" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="submit-issue-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Issue"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustmentsTab({ canCreate, canApprove, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const load = () => { setLoading(true); R.listAdjustments().then(setRows).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const decide = async (id, approve) => { try { await R.decideAdjustment(id, approve); toast.success(approve ? "Approved" : "Rejected"); load(); onChange?.(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  return (
    <Card className="p-4">
      <div className="mb-4 flex justify-end">{canCreate && <Button onClick={() => setOpen(true)} data-testid="add-adjustment-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><SlidersHorizontal className="mr-2 h-4 w-4" />New Adjustment</Button>}</div>
      {loading ? <Spin /> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><Th>Adj</Th><Th>Material</Th><Th right>Qty</Th><Th>Reason</Th><Th>Status</Th><Th right>Action</Th></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`adj-row-${r.adj_no}`}>
                  <TableCell className="font-medium text-slate-800">{r.adj_no}</TableCell>
                  <TableCell className="text-slate-700">{r.material_name}</TableCell>
                  <TableCell className={`text-right tabular-nums ${r.qty < 0 ? "text-rose-600" : "text-emerald-600"}`}>{r.qty > 0 ? "+" : ""}{r.qty} {r.unit}</TableCell>
                  <TableCell className="text-slate-600">{r.reason}</TableCell>
                  <TableCell><Badge className={r.status === "Approved" ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : r.status === "Rejected" ? "bg-rose-50 text-rose-700 hover:bg-rose-50" : "bg-amber-50 text-amber-700 hover:bg-amber-50"}>{r.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    {r.status === "Pending" && canApprove && (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => decide(r.id, true)} data-testid={`approve-adj-${r.adj_no}`}>Approve</Button>
                        <Button size="sm" variant="outline" className="text-rose-600" onClick={() => decide(r.id, false)} data-testid={`reject-adj-${r.adj_no}`}>Reject</Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-400" data-testid="adj-empty">No adjustments.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <AdjDialog open={open} onOpenChange={setOpen} onDone={() => { load(); onChange?.(); }} />
    </Card>
  );
}

function AdjDialog({ open, onOpenChange, onDone }) {
  const mats = useMaterials();
  const [f, setF] = useState({ material_id: "", qty: "", reason: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ material_id: "", qty: "", reason: "" }); }, [open]);
  const submit = async () => {
    if (!f.material_id || !Number(f.qty) || !f.reason) { toast.error("Material, non-zero qty and reason required"); return; }
    setBusy(true);
    try { const r = await R.createAdjustment({ material_id: f.material_id, qty: Number(f.qty), reason: f.reason }); toast.success(`${r.adj_no} submitted for approval`); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Stock Adjustment</DialogTitle><DialogDescription>Use a positive quantity to add stock, negative to reduce. Requires approval before it affects stock.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Material</Label>
            <Select value={f.material_id} onValueChange={(v) => setF({ ...f, material_id: v })}>
              <SelectTrigger data-testid="adj-material-select"><SelectValue placeholder="Select material" /></SelectTrigger>
              <SelectContent>{mats.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.on_hand} {m.unit})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Quantity delta (+/-)</Label><Input type="number" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="adj-qty-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Reason</Label><Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="adj-reason-input" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="submit-adj-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConsumptionTab() {
  const [rows, setRows] = useState([]);
  const [val, setVal] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { Promise.all([R.consumption(), R.valuation()]).then(([c, v]) => { setRows(c); setVal(v); }).finally(() => setLoading(false)); }, []);
  if (loading) return <Spin />;
  return (
    <div className="space-y-6">
      <Card className="p-4">
        <h3 className="mb-3 font-heading text-base font-semibold text-slate-900">Stock valuation by category</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="rm-valuation">
          {(val?.by_category || []).map((c) => <div key={c.category} className="rounded-md border border-slate-200 p-3"><p className="text-xs uppercase text-slate-500">{c.label}</p><p className="mt-1 font-heading text-sm font-bold text-slate-900">{inr(c.value)}</p></div>)}
          <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3"><p className="text-xs uppercase text-indigo-500">Total</p><p className="mt-1 font-heading text-sm font-bold text-indigo-700">{inr(val?.total_value)}</p></div>
        </div>
      </Card>
      <Card className="p-4">
        <h3 className="mb-3 font-heading text-base font-semibold text-slate-900">Consumption (issued to production)</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><Th>Material</Th><Th>Category</Th><Th right>Issued qty</Th><Th right>Issues</Th><Th right>Value</Th></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.material_id} data-testid={`consumption-row-${r.material_id}`}>
                  <TableCell className="font-medium text-slate-800">{r.material_name}</TableCell>
                  <TableCell className="text-slate-600">{CATEGORY_LABEL[r.category]}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{r.qty} {r.unit}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-500">{r.issues}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-700">{inr(r.value)}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-slate-400">No consumption yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

const Th = ({ children, right }) => <TableHead className={`text-xs uppercase text-slate-500 ${right ? "text-right" : ""}`}>{children}</TableHead>;
const Spin = () => <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
